import React, { useEffect, useState } from 'react'
import { Col, Row, Space } from 'antd'
import { appConfig } from 'config'
import { MapWithASearchBox } from 'Components/utilities'
import { DetailDrawer } from 'Components/common/MainLayout'
import { PrimaryButton } from 'Components/button'
import styled from 'styled-components'
import { AppInput } from 'Components/form'
import { formValidation, getAddressFormID, isFormError, isFormRequired } from 'helpers/format'
import { cloneDeep, trim } from 'lodash'
import AppAutocomplete from 'Components/form/AppAutocomplete'
import { AddressData, GetAddressReq } from 'Store/common/common.types'
import { OptionProps } from 'Components/form/InputAutocomplete'
import { MyTheme } from 'Components/common/Theme'
import { getAddressAPI } from 'Store/common/common.api'
import { connect, useDispatch } from 'react-redux'
import RootState from 'typings/RootState'
import { selectAddressData } from 'Store/common/common.selector'
import { clearEventAction } from 'Store/orders/actions'
import { selectUpdateOrderAddressEvent } from 'Store/orders/selector'
import { EventModal } from 'Components/modal'
import { UpdateOrderAddressReq } from 'Store/orders/types'
import { updateOrderAddressAPI } from 'Store/orders/api'
import { selectCurrentUser } from 'Store/user/selector'
import { Data } from 'Store/orders/types'
const MyMapWithASearchBox: any = MapWithASearchBox

type Props = {
  isShow: boolean
  onClose: () => void
  onSuccess: () => void
  data?: Data
} & ReduxProps

interface LocationState {
  lat: number
  long: number
}

interface InitForm {
  postcode: string
  address: string
  tel: string
  receiver_name: string
  address_item?: AddressData
}

const EditAddressDrawer = (props: Props) => {
  const requireFeild = ['address']

  const initForm = {
    postcode: '',
    address: '',
    tel: '',
    receiver_name: '',
    address_item: undefined,
  } as InitForm

  const { isShow, onClose, data, addressList, onSuccess, event, currentUser } = props
  const [formError, setFormError] = useState([] as string[])
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [location, setLocation] = useState({
    lat: 0,
    long: 0,
  } as LocationState)
  const dispatch = useDispatch()

  const onSave = () => {
    if (!validateForm()) return
    const req: UpdateOrderAddressReq = {
      order_no: data?.order_no || '',
      address: formData?.address || '',
      district_id: formData?.address_item?.district_id || 0,
      district: formData?.address_item?.district?.th || '',
      sub_district_id: formData?.address_item?.sub_district_id || 0,
      sub_district: formData?.address_item?.sub_district?.th || '',
      province_id: formData?.address_item?.province_id || 0,
      province: formData?.address_item?.province?.th || '',
      zipcode: formData?.address_item?.zipcode || '',
      lat: location.lat,
      long: location.long,
      tel: formData.tel,
      receiver_name: formData.receiver_name,
    }
    dispatch(updateOrderAddressAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  useEffect(() => {
    if (isShow) {
      setLocation({
        lat: data?.shipping_address?.lat || 0,
        long: data?.shipping_address?.long || 0,
      })
      setFormData({
        address: trim(data?.shipping_address?.address || ''),
        postcode: trim(data?.shipping_address?.zipcode || ''),
        receiver_name: trim(data?.shipping_address?.receiver_name || ''),
        tel: trim(data?.shipping_address?.tel || ''),
        address_item: {
          zipcode: trim(data?.shipping_address?.zipcode || ''),
          district: { th: trim(data?.shipping_address?.district || '') },
          district_id: data?.shipping_address?.district_id || 0,
          lat: data?.shipping_address?.lat,
          long: data?.shipping_address?.long || 0,
          province: { th: trim(data?.shipping_address?.province || '') },
          province_id: data?.shipping_address?.province_id || 0,
          sub_district: { th: trim(data?.shipping_address?.sub_district || '') },
          sub_district_id: data?.shipping_address?.sub_district_id || 0,
        },
      })
    }
  }, [data, isShow])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  useEffect(() => {
    if (!isShow) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }

    return () => {
      setFormData({ ...initForm })
    }
  }, [isShow])

  const onGetAddressList = (): void => {
    const req: GetAddressReq = {
      keyword: formData.postcode,
    }
    dispatch(getAddressAPI(req))
  }

  useEffect(() => {
    if (formData?.postcode && formData.postcode?.length === 5) {
      onGetAddressList()
    } else {
      onUpdateData(undefined, 'address_item')
    }
  }, [formData.postcode])

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const onUpdateData = (val: any, key: string) => {
    if (key === 'tel' && val.length > 10) return
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const onSelectAddress = (id: number) => {
    const addressItem = getAddressFormID(addressList, id)
    onUpdateData(addressItem, 'address_item')
    if (addressItem) {
      setLocation({
        lat: addressItem?.lat || 0,
        long: addressItem?.long || 0,
      })
    }
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const footer = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onClose} />
      <PrimaryButton text="บันทึก" onClick={onSave} />
    </Space>
  )

  const getAddressOptions = (data: AddressData[]) => {
    const options = [] as OptionProps[]
    data &&
      data.forEach(item => {
        options.push({
          value: item.id,
          //label: `${item.sub_district.name} / ${item.district.name} /  ${item.province.name} `,
          label: (
            <AddressWrapper>
              <Row gutter={[1, 1]}>
                <Col span={8}>{item?.sub_district?.th || ''} </Col>
                <Col span={8}>{item?.district?.th || ''} </Col>
                <Col span={8}>{item?.province?.th || ''}</Col>
              </Row>
            </AddressWrapper>
          ),
        })
      })
    return options
  }

  return (
    <DetailDrawer
      title="แก้ไขที่อยู่จัดส่ง"
      width={'50%'}
      onCloseDrawer={onClose}
      visible={isShow}
      footer={footer}
      maskClosable={true}
    >
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row gutter={[8, 8]}>
        {/* <Col md={12} xs={24}>
          <AppInput
            name="receiver_name"
            label="ชื่อผู้รับ"
            isRequired={isRequired('receiver_name')}
            isError={isError('receiver_name')}
            value={formData.receiver_name}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tel"
            label="หมายเลขโทรศัพท์ที่ติดต่อได้"
            isRequired={isRequired('tel')}
            isError={isError('tel')}
            value={formData.tel}
            onChange={onUpdateData}
          />
        </Col> */}
        <Col md={24} xs={24}>
          <AppInput
            name="address"
            label="ที่อยู่"
            placeHolder="บ้านเลขที่ ชื่อหมู่บ้าน/ชื่อคอนโด ซอย ถนน"
            isRequired={isRequired('address')}
            isError={isError('address')}
            value={formData.address}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppAutocomplete
            labelColor="#000"
            name="postcode"
            label="รหัสไปรษณีย์"
            placeHolder="ระบุรหัสไปรษณีย์ 5 หลัก"
            isRequired={isRequired('postcode')}
            isError={isError('postcode')}
            value={formData.postcode}
            onChange={val => onSelectAddress(val)}
            onSearch={val => onUpdateData(val, 'postcode')}
            options={formData?.postcode?.length === 5 ? getAddressOptions(addressList) : []}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="sub_district_id"
            label="แขวง/ตำบล"
            isRequired={isRequired('address_item')}
            isError={isError('address_item')}
            value={formData.address_item?.sub_district?.th}
            onChange={onUpdateData}
            resProps={{ disabled: true }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="district_id"
            label="เขต/อำเภอ"
            isRequired={isRequired('address_item')}
            isError={isError('address_item')}
            value={formData.address_item?.district?.th}
            onChange={onUpdateData}
            resProps={{ disabled: true }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="province_id"
            label="จังหวัด"
            isRequired={isRequired('address_item')}
            isError={isError('address_item')}
            value={formData.address_item?.province?.th}
            onChange={onUpdateData}
            resProps={{ disabled: true }}
          />
        </Col>
        <Col span={24}>
          ระบุตำแหน่ง (เลื่อนหมุดไปยังจุดจัดส่ง)
          <MapWrapper>
            <MyMapWithASearchBox
              lat={location.lat}
              long={location.long}
              isMarkerShown
              draggable={true}
              onDragEnd={(lat: number, lng: number) => {
                setLocation({
                  lat: lat,
                  long: lng,
                })
              }}
              googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${appConfig.googleMapKey}&v=3.exp&libraries=geometry,drawing,places`}
              loadingElement={<div style={{ height: `100%` }} />}
              containerElement={<div style={{ height: `60vh` }} />}
              mapElement={<div style={{ height: `100%` }} />}
              icon="consumer"
            />
          </MapWrapper>
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const MapWrapper = styled.div`
  padding: 10px 0;
  .gmnoprint {
    bottom: 12px;
    top: auto !important;
  }
  // .ant-input-affix-wrapper {
  //   margin: 1%;
  //   width: 80% !important;
  // }
`

const AddressWrapper = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  @media only screen and (max-width: 720px) {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  }
`

const mapStateToProps = (state: RootState) => {
  return {
    addressList: selectAddressData(state),
    currentUser: selectCurrentUser(state),
    event: selectUpdateOrderAddressEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(EditAddressDrawer)
